import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import QTemplate from "../QTemplate";
import InTemplate from "../InTemplate";
import ContactAdminTemplate from "../ContactAdminTemplate";
import { useAuth } from '../../AuthContext'; 
import { db } from '../../firebase.config';
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";

export function ContactAdminRedirect() {
  const { isAdmin } = useUserContext();
  return isAdmin ? <QTemplate /> : <ContactAdminTemplate type="quotation" />;
}

export function InvoiceAdminRedirect() {
  const { isAdmin } = useUserContext();
  return isAdmin ? <InTemplate /> : <ContactAdminTemplate type="invoice" />;
}

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [profileDocId, setProfileDocId] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (currentUser && currentUser.email) {
        try {
          // Instances differ: some profiles store "email", others "userEmail". Try both.
          let querySnapshot = await getDocs(query(collection(db, "UserProfileMaster"), where("email", "==", currentUser.email)));
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(query(collection(db, "UserProfileMaster"), where("userEmail", "==", currentUser.email)));
          }

          if (!querySnapshot.empty) {
            const profileData = querySnapshot.docs[0].data();
            setProfileDocId(querySnapshot.docs[0].id);
            setUserProfile(profileData);
            
            // Fetch all admin settings from Firestore if tenantId exists
            if (profileData.tenantId) {
              try {
                const settingsRef = doc(db, `tenants/${profileData.tenantId}/settings`, 'templates');
                const settingsSnap = await getDoc(settingsRef);
                if (settingsSnap.exists()) {
                  const settings = settingsSnap.data();
                  
                  // Sync Invoice settings
                  if (settings.invoiceTemplate) localStorage.setItem("adminSelectedTemplate", settings.invoiceTemplate);
                  if (settings.invoiceLogo) localStorage.setItem("invoiceLogo", settings.invoiceLogo);
                  if (settings.invoiceBanner) localStorage.setItem("invoiceBanner", settings.invoiceBanner);
                  if (settings.invoiceBusinessName) localStorage.setItem("invoiceBusinessName", settings.invoiceBusinessName);
                  if (settings.invoiceBusinessAddress) localStorage.setItem("invoiceBusinessAddress", settings.invoiceBusinessAddress);
                  if (settings.invoiceFooter) localStorage.setItem("invoiceFooter", settings.invoiceFooter);
                  if (settings.invoiceDefaultAdditionalNotes) localStorage.setItem("invoiceDefaultAdditionalNotes", settings.invoiceDefaultAdditionalNotes);
                  if (settings.invoiceDefaultTermsAndConditions) localStorage.setItem("invoiceDefaultTermsAndConditions", settings.invoiceDefaultTermsAndConditions);
                  if (settings.invoiceDefaultAuthorizedSignature) localStorage.setItem("invoiceDefaultAuthorizedSignature", settings.invoiceDefaultAuthorizedSignature);
                  if (settings.invoiceDefaultAuthorizedSignatureName) localStorage.setItem("invoiceDefaultAuthorizedSignatureName", settings.invoiceDefaultAuthorizedSignatureName);
                  if (settings.invoiceCurrency) localStorage.setItem("invoiceCurrency", JSON.stringify(settings.invoiceCurrency));
                  if (settings.invoiceIdPrefix) localStorage.setItem("invoiceIdPrefix", settings.invoiceIdPrefix);
                  if (settings.invoiceCustomCounter) localStorage.setItem("invoiceCustomCounter", settings.invoiceCustomCounter);
                  if (settings.showInvoiceGST !== undefined) localStorage.setItem("showInvoiceGST", JSON.stringify(settings.showInvoiceGST));
                  
                  // Sync Quotation settings
                  if (settings.quotationTemplate) localStorage.setItem("adminSelectedQuotationTemplate", settings.quotationTemplate);
                  if (settings.quotationLogo) localStorage.setItem("quotationLogo", settings.quotationLogo);
                  if (settings.quotationBanner) localStorage.setItem("quotationBanner", settings.quotationBanner);
                  if (settings.quotationBusinessName) localStorage.setItem("quotationBusinessName", settings.quotationBusinessName);
                  if (settings.quotationBusinessAddress) localStorage.setItem("quotationBusinessAddress", settings.quotationBusinessAddress);
                  if (settings.quotationFooter) localStorage.setItem("quotationFooter", settings.quotationFooter);
                  if (settings.quotationDefaultAdditionalNotes) localStorage.setItem("quotationDefaultAdditionalNotes", settings.quotationDefaultAdditionalNotes);
                  if (settings.quotationDefaultTermsAndConditions) localStorage.setItem("quotationDefaultTermsAndConditions", settings.quotationDefaultTermsAndConditions);
                  if (settings.quotationDefaultAuthorizedSignature) localStorage.setItem("quotationDefaultAuthorizedSignature", settings.quotationDefaultAuthorizedSignature);
                  if (settings.quotationDefaultAuthorizedSignatureName) localStorage.setItem("quotationDefaultAuthorizedSignatureName", settings.quotationDefaultAuthorizedSignatureName);
                  if (settings.currency) localStorage.setItem("currency", JSON.stringify(settings.currency));
                  if (settings.quotationIdPrefix) localStorage.setItem("quotationIdPrefix", settings.quotationIdPrefix);
                  if (settings.quotationCustomCounter) localStorage.setItem("quotationCustomCounter", settings.quotationCustomCounter);
                }
              } catch (err) {
                console.warn("Could not fetch settings from Firestore:", err);
              }
            }
          } else {
            setUserProfile(null);
          setProfileDocId(null);
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setUserProfile(null);
          setProfileDocId(null);
        }
      } else {
        setUserProfile(null);
          setProfileDocId(null);
      }
    };
    setProfileLoaded(false);
    fetchUserProfile().finally(() => setProfileLoaded(true));
  }, [currentUser]);

  // --- MODIFIED: Created independent states for Invoice and Quotation ---
  
  // Generic states
  const adminval = localStorage.getItem("IsAdmin");
  const [isAdmin, setIsAdmin] = useState(adminval === "true" ? true : false);
  const [userId, setUserId] = useState(() => localStorage.getItem("userId") || null);

  // Invoice-specific states
  const [invoiceLogo, setInvoiceLogo] = useState(() => localStorage.getItem("invoiceLogo") || null);
  const [invoiceBanner, setInvoiceBanner] = useState(() => localStorage.getItem("invoiceBanner") || null);
  const [invoiceBusinessName, setInvoiceBusinessName] = useState(() => localStorage.getItem("invoiceBusinessName") || "");
  const [invoiceBusinessAddress, setInvoiceBusinessAddress] = useState(() => localStorage.getItem("invoiceBusinessAddress") || "");
  const [invoiceFooter, setInvoiceFooter] = useState(() => localStorage.getItem("invoiceFooter") || "");
  const [adminSelectedTemplate, setAdminSelectedTemplate] = useState(() => localStorage.getItem("adminSelectedTemplate") || "contact-admin");
  const [invoiceCurrency, setInvoiceCurrency] = useState(() => {
    const saved = localStorage.getItem("invoiceCurrency");
    return saved ? JSON.parse(saved) : { code: "INR", name: "Indian Rupee", symbol: "₹" };
  });
  const [invoiceDefaultAdditionalNotes, setInvoiceDefaultAdditionalNotes] = useState(() => localStorage.getItem("invoiceDefaultAdditionalNotes") || "");
  const [invoiceDefaultTermsAndConditions, setInvoiceDefaultTermsAndConditions] = useState(() => localStorage.getItem("invoiceDefaultTermsAndConditions") || "");
  const [invoiceDefaultAuthorizedSignature, setInvoiceDefaultAuthorizedSignature] = useState(() => localStorage.getItem("invoiceDefaultAuthorizedSignature") || "");
  const [invoiceDefaultAuthorizedSignatureName, setInvoiceDefaultAuthorizedSignatureName] = useState(() => localStorage.getItem("invoiceDefaultAuthorizedSignatureName") || "");

  // Quotation-specific states
  const [quotationLogo, setQuotationLogo] = useState(() => localStorage.getItem("quotationLogo") || null);
  const [quotationBanner, setQuotationBanner] = useState(() => localStorage.getItem("quotationBanner") || null);
  const [quotationBusinessName, setQuotationBusinessName] = useState(() => localStorage.getItem("quotationBusinessName") || "");
  const [quotationBusinessAddress, setQuotationBusinessAddress] = useState(() => localStorage.getItem("quotationBusinessAddress") || "");
  const [quotationFooter, setQuotationFooter] = useState(() => localStorage.getItem("quotationFooter") || "");
  const [adminSelectedQuotationTemplate, setAdminSelectedQuotationTemplate] = useState(() => localStorage.getItem("adminSelectedQuotationTemplate") || "contact-admin");
  const [quotationValidityDays, setQuotationValidityDays] = useState(() => localStorage.getItem("quotationValidityDays") || 7);
  const [currency, setCurrency] = useState(() => {
      const saved = localStorage.getItem("currency");
      return saved ? JSON.parse(saved) : { code: "INR", name: "Indian Rupee", symbol: "₹" };
  });
  const [quotationDefaultAdditionalNotes, setQuotationDefaultAdditionalNotes] = useState(() => localStorage.getItem("quotationDefaultAdditionalNotes") || "");
  const [quotationDefaultTermsAndConditions, setQuotationDefaultTermsAndConditions] = useState(() => localStorage.getItem("quotationDefaultTermsAndConditions") || "");
  const [quotationDefaultAuthorizedSignature, setQuotationDefaultAuthorizedSignature] = useState(() => localStorage.getItem("quotationDefaultAuthorizedSignature") || "");
  const [quotationDefaultAuthorizedSignatureName, setQuotationDefaultAuthorizedSignatureName] = useState(() => localStorage.getItem("quotationDefaultAuthorizedSignatureName") || "");
  // Quotation ID prefix (admin controlled) and timestamp when it was set
  const [quotationIdPrefix, setQuotationIdPrefix] = useState(() => localStorage.getItem("quotationIdPrefix") || "");
  const [quotationPrefixSetAt, setQuotationPrefixSetAt] = useState(() => localStorage.getItem("quotationPrefixSetAt") || null);
  // Invoice ID prefix (admin controlled) and timestamp when it was set
  const [invoiceIdPrefix, setInvoiceIdPrefix] = useState(() => localStorage.getItem("invoiceIdPrefix") || "");
  const [invoicePrefixSetAt, setInvoicePrefixSetAt] = useState(() => localStorage.getItem("invoicePrefixSetAt") || null);
  // Custom counter starting number for invoices (admin can set any number format)
  const [invoiceCustomCounter, setInvoiceCustomCounter] = useState(() => localStorage.getItem("invoiceCustomCounter") || "0");
  // Timestamp when invoice counter was set (for locking mechanism)
  const [invoiceCounterSetAt, setInvoiceCounterSetAt] = useState(() => localStorage.getItem("invoiceCounterSetAt") || null);
  // Custom counter starting number for quotations (admin can set any number format)
  const [quotationCustomCounter, setQuotationCustomCounter] = useState(() => localStorage.getItem("quotationCustomCounter") || "0");
  // Timestamp when quotation counter was set (for locking mechanism)
  const [quotationCounterSetAt, setQuotationCounterSetAt] = useState(() => localStorage.getItem("quotationCounterSetAt") || null);
  // GST/Tax toggle for invoices (admin can enable/disable GST display)
  const [showInvoiceGST, setShowInvoiceGST] = useState(() => {
    const saved = localStorage.getItem("showInvoiceGST");
    return saved !== null ? JSON.parse(saved) : true; // Default to true (GST enabled)
  });

  // --- Persistence Effects ---
  const useLocalStorage = (key, value) => {
    useEffect(() => {
      if (value) {
        localStorage.setItem(key, value);
      } else {
        localStorage.removeItem(key);
      }
    }, [key, value]);
  };
  
  const useJsonLocalStorage = (key, value) => {
      useEffect(() => {
          localStorage.setItem(key, JSON.stringify(value));
      }, [key, value]);
  };

  // Persist all invoice states
  useLocalStorage("invoiceLogo", invoiceLogo);
  useLocalStorage("invoiceBanner", invoiceBanner);
  useLocalStorage("invoiceBusinessName", invoiceBusinessName);
  useLocalStorage("invoiceBusinessAddress", invoiceBusinessAddress);
  useLocalStorage("invoiceFooter", invoiceFooter);
  useLocalStorage("adminSelectedTemplate", adminSelectedTemplate);
  useJsonLocalStorage("invoiceCurrency", invoiceCurrency);
  useLocalStorage("invoiceDefaultAdditionalNotes", invoiceDefaultAdditionalNotes);
  useLocalStorage("invoiceDefaultTermsAndConditions", invoiceDefaultTermsAndConditions);
  useLocalStorage("invoiceDefaultAuthorizedSignature", invoiceDefaultAuthorizedSignature);
  useLocalStorage("invoiceDefaultAuthorizedSignatureName", invoiceDefaultAuthorizedSignatureName);

  // Persist all quotation states
  useLocalStorage("quotationLogo", quotationLogo);
  useLocalStorage("quotationBanner", quotationBanner);
  useLocalStorage("quotationBusinessName", quotationBusinessName);
  useLocalStorage("quotationBusinessAddress", quotationBusinessAddress);
  useLocalStorage("quotationFooter", quotationFooter);
  useLocalStorage("adminSelectedQuotationTemplate", adminSelectedQuotationTemplate);
  useLocalStorage("quotationValidityDays", quotationValidityDays);
  useJsonLocalStorage("currency", currency);
  useLocalStorage("quotationDefaultAdditionalNotes", quotationDefaultAdditionalNotes);
  useLocalStorage("quotationDefaultTermsAndConditions", quotationDefaultTermsAndConditions);
  useLocalStorage("quotationDefaultAuthorizedSignature", quotationDefaultAuthorizedSignature);
  useLocalStorage("quotationDefaultAuthorizedSignatureName", quotationDefaultAuthorizedSignatureName);
  useLocalStorage("quotationIdPrefix", quotationIdPrefix);
  useLocalStorage("quotationPrefixSetAt", quotationPrefixSetAt);
  useLocalStorage("invoiceIdPrefix", invoiceIdPrefix);
  useLocalStorage("invoicePrefixSetAt", invoicePrefixSetAt);
  
  // Persist custom counter values and their timestamps
  useEffect(() => {
    localStorage.setItem("invoiceCustomCounter", invoiceCustomCounter);
  }, [invoiceCustomCounter]);
  
  useEffect(() => {
    localStorage.setItem("invoiceCounterSetAt", invoiceCounterSetAt);
  }, [invoiceCounterSetAt]);
  
  useEffect(() => {
    localStorage.setItem("quotationCustomCounter", quotationCustomCounter);
  }, [quotationCustomCounter]);
  
  useEffect(() => {
    localStorage.setItem("quotationCounterSetAt", quotationCounterSetAt);
  }, [quotationCounterSetAt]);

  // Persist GST toggle
  useEffect(() => {
    localStorage.setItem("showInvoiceGST", JSON.stringify(showInvoiceGST));
  }, [showInvoiceGST]);

  // Generic persistence
  useEffect(() => { localStorage.setItem("IsAdmin", isAdmin); }, [isAdmin]);
  
  const setUserIdCallback = useCallback((id) => {
    setUserId(id);
    if (id) {
      localStorage.setItem("userId", id);
    } else {
      localStorage.removeItem("userId");
    }
  }, []);

  const contextValue = {
    userProfile,
    profileDocId,
    profileLoaded,
    isAdmin, setIsAdmin,
    userId, setUserId: setUserIdCallback,

    // Invoice context values
    invoiceLogo, setInvoiceLogo,
    invoiceBanner, setInvoiceBanner,
    invoiceBusinessName, setInvoiceBusinessName,
    invoiceBusinessAddress, setInvoiceBusinessAddress,
    invoiceFooter, setInvoiceFooter,
    adminSelectedTemplate, setAdminSelectedTemplate,
    invoiceCurrency, setInvoiceCurrency,
    invoiceDefaultAdditionalNotes, setInvoiceDefaultAdditionalNotes,
    invoiceDefaultTermsAndConditions, setInvoiceDefaultTermsAndConditions,
    invoiceDefaultAuthorizedSignature, setInvoiceDefaultAuthorizedSignature,
    invoiceDefaultAuthorizedSignatureName, setInvoiceDefaultAuthorizedSignatureName,

    // Quotation context values
    quotationLogo, setQuotationLogo,
    quotationBanner, setQuotationBanner,
    quotationBusinessName, setQuotationBusinessName,
    quotationBusinessAddress, setQuotationBusinessAddress,
    quotationFooter, setQuotationFooter,
    adminSelectedQuotationTemplate, setAdminSelectedQuotationTemplate,
    quotationValidityDays, setQuotationValidityDays,
    currency, setCurrency,
    quotationDefaultAdditionalNotes, setQuotationDefaultAdditionalNotes,
    quotationDefaultTermsAndConditions, setQuotationDefaultTermsAndConditions,
    quotationDefaultAuthorizedSignature, setQuotationDefaultAuthorizedSignature,
    quotationDefaultAuthorizedSignatureName, setQuotationDefaultAuthorizedSignatureName,
    quotationIdPrefix, setQuotationIdPrefix,
    quotationPrefixSetAt, setQuotationPrefixSetAt,
    // Invoice prefix values
    invoiceIdPrefix, setInvoiceIdPrefix,
    invoicePrefixSetAt, setInvoicePrefixSetAt,
    // Custom counter values and timestamps
    invoiceCustomCounter, setInvoiceCustomCounter,
    invoiceCounterSetAt, setInvoiceCounterSetAt,
    quotationCustomCounter, setQuotationCustomCounter,
    quotationCounterSetAt, setQuotationCounterSetAt,
    // GST toggle for invoices
    showInvoiceGST, setShowInvoiceGST,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};